// IE Hub - Notes Dialog Component
// Dialog for adding explanatory notes to form fields
// Mobile: bottom slide, full page | Desktop: right slide, partial

import React, { useState, useEffect } from 'react';
import {
  Box,
  CloseIcon,
  Dialog,
  DialogContent,
  Divider,
  Drawer,
  IconButton,
  Slide,
  Typography,
  useMediaQuery,
  useTheme,
  UIButton as Button,
  TextField,
} from '../ui';
import { TransitionProps } from '@mui/material/transitions';

interface NotesDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (noteText: string) => void;
  fieldLabel: string;
  initialValue?: string;
  zIndex?: number;
  // Optional: instead of simple textarea, render a JSON-driven Screen inside the dialog
  embeddedSchema?: any;
  onEmbeddedSubmit?: (data: Record<string, unknown>) => void;
}

// Slide transition for mobile (from bottom)
const SlideUpTransition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} appear={props.appear ?? true} />;
});

export default function NotesDialog({
  open,
  onClose,
  onSave,
  fieldLabel,
  initialValue = '',
  zIndex = 1303,
  embeddedSchema,
  onEmbeddedSubmit,
}: NotesDialogProps): JSX.Element {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [noteText, setNoteText] = useState(initialValue);

  // Update local state when initialValue changes
  useEffect(() => {
    if (open) {
      setNoteText(initialValue);
    }
  }, [open, initialValue]);

  const handleSave = () => {
    onSave(noteText);
    onClose();
  };

  const handleCancel = () => {
    setNoteText(initialValue); // Reset to initial value
    onClose();
  };

  const dialogContent = (
    <Box data-testid="components-notes-dialog-container-action-1"
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}
    >
      {/* Header */}
      <Box data-testid="components-notes-dialog-container-action-2"
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: { xs: 2, md: 3 },
          minHeight: '64px',
        }}
      >
        <Typography data-testid="components-notes-dialog-text-add-a-note" variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
          Add a Note
        </Typography>
        <IconButton data-testid="components-notes-dialog-btn-handle-cancel-1"
          onClick={handleCancel}
          sx={{
            p: 1,
            backgroundColor: 'action.hover',
            '&:hover': {
              backgroundColor: 'action.selected',
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Header divider — inset (mx) so the line doesn't touch the drawer borders, per Figma */}
      <Divider sx={{ mx: { xs: 2, md: 3 } }} />

      {/* Content */}
      <Box data-testid="components-notes-dialog-container-action-3"
        sx={{
          flex: 1,
          p: { xs: 2, md: 3 },
          overflow: 'auto',
        }}
      >
        {embeddedSchema ? (
          // Render embedded JSON-driven screen with only a Save footer handled below
          // We import Screen dynamically at call sites where we pass embeddedSchema
          // so here we just display helper text; actual embedding is done by parent or via portal.
          <Typography data-testid="components-notes-dialog-text-embedded-form-is-loading..." variant="body2" sx={{ color: 'text.secondary' }}>
            Embedded form is loading...
          </Typography>
        ) : (
          <>
            <Typography data-testid="components-notes-dialog-text-action"
              variant="body2"
              sx={{
                mb: 2,
                color: 'text.secondary',
                lineHeight: 1.6,
              }}
            >
              Please provide additional information for <strong>{fieldLabel}</strong>. This note
              will help creditors better understand your situation.
            </Typography>

            <TextField data-testid="components-notes-dialog-input-type-your-explanation-here..."
              multiline
              rows={6}
              maxRows={12}
              fullWidth
              value={noteText}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNoteText(e.target.value)}
              placeholder="Type your explanation here..."
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'background.paper',
                },
              }}
            />
          </>
        )}
      </Box>

      {/* Footer with action buttons */}
      <Box data-testid="components-notes-dialog-container-action-4"
        sx={{
          p: { xs: 2, md: 3 },
          borderTop: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          gap: 2,
          justifyContent: 'flex-end',
        }}
      >
        <Button data-testid="components-notes-dialog-btn-handle-cancel-2"
          variant="outlined"
          onClick={handleCancel}
          sx={{
            minWidth: '100px',
          }}
        >
          Cancel
        </Button>
        <Button data-testid="components-notes-dialog-btn-save"
          variant="contained"
          onClick={() => {
            if (embeddedSchema && onEmbeddedSubmit) {
              onEmbeddedSubmit({});
            } else {
              handleSave();
            }
          }}
          sx={{
            minWidth: '100px',
          }}
        >
          Save
        </Button>
      </Box>
    </Box>
  );

  if (isMobile) {
    // Mobile: Full-screen dialog sliding from bottom
    return (
      <Dialog data-testid="components-notes-dialog-modal-action"
        open={open}
        onClose={handleCancel}
        TransitionComponent={SlideUpTransition}
        fullScreen
        sx={{
          zIndex: zIndex,
          '& .MuiDialog-paper': {
            backgroundColor: 'background.default',
            backgroundImage: 'none',
            margin: 0,
            borderRadius: '16px 16px 0 0',
          },
        }}
      >
        <DialogContent sx={{ p: 0, height: '100%' }}>
          {/* Swipe Indicator */}
          <Box data-testid="components-notes-dialog-container-action-5"
            sx={{
              display: 'flex',
              justifyContent: 'center',
              pt: 1,
              pb: 0.5,
            }}
          >
            <Box data-testid="components-notes-dialog-container-action-6"
              sx={{
                width: 36,
                height: 4,
                backgroundColor: 'action.disabled',
                borderRadius: 2,
                opacity: 0.6,
              }}
            />
          </Box>
          {dialogContent}
        </DialogContent>
      </Dialog>
    );
  }

  // Desktop: Drawer sliding from right
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleCancel}
      sx={{
        zIndex: zIndex,
        '& .MuiDrawer-paper': {
          width: { md: '400px', lg: '480px' },
          maxWidth: '40vw',
          backgroundColor: 'background.paper',
          border: 'none',
          boxShadow: theme.shadows[8],
          borderRadius: '16px 0 0 16px',
        },
      }}
    >
      {dialogContent}
    </Drawer>
  );
}

